import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Calendar, DollarSign, TrendingUp, Ticket, Download } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const RANGES = [
  { key: "day", label: "Hoy" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mes" },
  { key: "all", label: "Todas" },
];

export default function AdminDashboard() {
  const [range, setRange] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const load = async (r = range, s = "", e = "") => {
    const params = new URLSearchParams();
    if (r === "custom" && s && e) {
      params.set("range", "custom");
      params.set("start", new Date(s).toISOString());
      params.set("end", new Date(e + "T23:59:59").toISOString());
    } else {
      params.set("range", r);
    }
    const res = await api.get(`/admin/bookings?${params.toString()}`);
    setBookings(res.data);
  };

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line
  }, []);

  const markPaid = async (bookingId) => {
    if (!window.confirm("¿Confirmas que el pago llegó a Stripe? Esto marcará la reserva como pagada y enviará el ticket por email al cliente.")) return;
    try {
      await api.post(`/admin/bookings/${bookingId}/mark-paid`);
      await load();
      alert("✅ Marcado como pagado y email del ticket enviado");
    } catch (e) {
      alert("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const resendTicket = async (bookingId) => {
    if (!window.confirm("¿Reenviar el email del ticket al cliente?")) return;
    try {
      await api.post(`/admin/bookings/${bookingId}/resend-ticket`);
      alert("✅ Ticket reenviado por email");
    } catch (e) {
      alert("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const changeRange = (k) => {
    setRange(k);
    if (k !== "custom") load(k);
  };

  const applyCustom = () => {
    if (customStart && customEnd) {
      setRange("custom");
      load("custom", customStart, customEnd);
    }
  };

  const downloadMonthPDF = async () => {
    // Fetch month data fresh
    const res = await api.get("/admin/bookings?range=month");
    const rows = res.data || [];
    const doc = new jsPDF({ orientation: "landscape", unit: "pt" });

    const today = new Date();
    const from = new Date(today.getTime() - 30 * 24 * 3600 * 1000);
    const fmt = (d) => d.toISOString().slice(0, 10);

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("BENOVA TRAVEL GROUP", 40, 50);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text("Riviera Maya — Reporte mensual de reservas", 40, 68);
    doc.text(`Periodo: ${fmt(from)} → ${fmt(today)}`, 40, 84);
    doc.text(`Generado: ${today.toLocaleString()}`, 40, 100);

    // Summary
    const paid = rows.filter((r) => r.status === "paid");
    const revenue = paid.reduce((s, r) => s + Number(r.total_amount || 0), 0);
    doc.setTextColor(0);
    doc.setFontSize(11);
    doc.text(`Reservas totales: ${rows.length}`, 40, 130);
    doc.text(`Reservas pagadas: ${paid.length}`, 220, 130);
    doc.text(`Ingresos confirmados: $${revenue.toFixed(2)} USD`, 400, 130);

    // Table
    autoTable(doc, {
      startY: 150,
      head: [["Código", "Cliente", "Email", "Tel/WA", "Experiencia", "Fecha", "Personas", "Total", "Estado", "Cupón"]],
      body: rows.map((r) => [
        r.id.slice(0, 8).toUpperCase(),
        r.customer_name,
        r.customer_email,
        r.customer_phone,
        r.experience_name,
        r.booking_date,
        String(r.num_people),
        `$${r.total_amount} ${r.currency}`,
        r.status,
        r.coupon_code || "-",
      ]),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [10, 10, 10], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 248, 243] },
      columnStyles: { 0: { fontStyle: "bold" } },
      didDrawPage: (data) => {
        const pc = doc.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`Página ${data.pageNumber} / ${pc}`, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 20);
      },
    });

    doc.save(`benova-reservas-${fmt(today)}.pdf`);
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-dashboard">
      <div className="bn-eyebrow">Panel de control</div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="bn-heading text-3xl md:text-4xl mt-2">Dashboard</h1>
        <button onClick={downloadMonthPDF} className="bn-btn-primary inline-flex items-center gap-2" data-testid="download-pdf-btn">
          <Download size={16} /> Descargar PDF del mes
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <StatCard label="Hoy" stats={stats.day} icon={Calendar} />
          <StatCard label="Semana" stats={stats.week} icon={TrendingUp} />
          <StatCard label="Mes" stats={stats.month} icon={DollarSign} />
          <StatCard label="Total" stats={stats.all} icon={Ticket} />
        </div>
      )}

      {/* Coupon usage */}
      {stats?.month?.coupons?.length > 0 && (
        <div className="mt-8 bn-card p-5 md:p-6" data-testid="coupons-usage-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="bn-eyebrow" style={{ color: "var(--bn-accent)" }}>Cupones usados</div>
              <div className="bn-heading text-lg mt-1">Últimos 30 días</div>
            </div>
            <div className="text-right">
              <div className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>Total descontado este mes</div>
              <div className="bn-heading text-xl" style={{ color: "var(--bn-accent)" }}>- ${(stats.month.discount_given || 0).toFixed(2)}</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--bn-border)" }}>
                  <Th>Cupón</Th>
                  <Th>Usos</Th>
                  <Th>Descontado</Th>
                  <Th>Ingreso neto</Th>
                </tr>
              </thead>
              <tbody>
                {stats.month.coupons.map((c) => (
                  <tr key={c.code} style={{ borderBottom: "1px solid var(--bn-border)" }} data-testid={`coupon-row-${c.code}`}>
                    <Td><span className="bn-heading tracking-wider">{c.code}</span></Td>
                    <Td>{c.uses}</Td>
                    <Td style={{ color: "var(--bn-accent)" }}>- ${c.discount.toFixed(2)}</Td>
                    <Td><b>${c.revenue.toFixed(2)}</b></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Range filter */}
      <div className="mt-10 flex flex-wrap gap-2 items-center">
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => changeRange(r.key)}
            className="bn-eyebrow px-4 py-2"
            style={{
              borderRadius: "var(--bn-button-radius)",
              border: "1px solid var(--bn-border)",
              background: range === r.key ? "var(--bn-primary)" : "transparent",
              color: range === r.key ? "var(--bn-primary-fg)" : "var(--bn-text)",
            }}
            data-testid={`range-${r.key}`}
          >
            {r.label}
          </button>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="bn-input" style={{ padding: ".5rem .75rem" }} data-testid="custom-start" />
          <span style={{ color: "var(--bn-text-muted)" }}>→</span>
          <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="bn-input" style={{ padding: ".5rem .75rem" }} data-testid="custom-end" />
          <button onClick={applyCustom} className="bn-btn-ghost" data-testid="custom-apply">Aplicar</button>
        </div>
      </div>

      {/* Bookings table */}
      <div className="mt-8 bn-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--bn-surface)" }}>
                <Th>Código</Th>
                <Th>Cliente</Th>
                <Th>Experiencia</Th>
                <Th>Fecha</Th>
                <Th>Pers.</Th>
                <Th>Subtotal</Th>
                <Th>Cupón</Th>
                <Th>Descuento</Th>
                <Th>Total pagado</Th>
                <Th>Estado</Th>
                <Th>Ticket</Th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && (
                <tr><td colSpan={11} className="text-center py-12" style={{ color: "var(--bn-text-muted)" }}>Sin reservas en este rango.</td></tr>
              )}
              {bookings.map((b) => {
                const subtotal = Number(b.total_amount || 0);
                const finalAmount = b.final_amount !== undefined && b.final_amount !== null ? Number(b.final_amount) : subtotal;
                const hasDiscount = b.coupon_code && finalAmount < subtotal;
                const discount = subtotal - finalAmount;
                return (
                <tr key={b.id} className="border-t" style={{ borderColor: "var(--bn-border)" }} data-testid={`booking-row-${b.id}`}>
                  <Td><span className="tracking-wider">{b.id.slice(0, 8).toUpperCase()}</span></Td>
                  <Td>
                    <div>{b.customer_name}</div>
                    <div className="text-xs" style={{ color: "var(--bn-text-muted)" }}>{b.customer_email}</div>
                  </Td>
                  <Td>{b.experience_name}</Td>
                  <Td>{b.booking_date}</Td>
                  <Td>{b.num_people}</Td>
                  <Td><span style={{ color: hasDiscount ? "var(--bn-text-muted)" : "var(--bn-text)" }}>${subtotal.toFixed(2)}</span></Td>
                  <Td>
                    {b.coupon_code ? (
                      <span className="bn-eyebrow tracking-wider" style={{ color: "var(--bn-accent)" }}>{b.coupon_code}</span>
                    ) : (
                      <span style={{ color: "var(--bn-text-muted)" }}>—</span>
                    )}
                  </Td>
                  <Td>{hasDiscount ? <span style={{ color: "#16a34a" }}>- ${discount.toFixed(2)}</span> : <span style={{ color: "var(--bn-text-muted)" }}>—</span>}</Td>
                  <Td><b>${finalAmount.toFixed(2)}</b> {b.currency}</Td>
                  <Td>
                    <span
                      className="inline-block px-2.5 py-1 text-xs rounded"
                      style={{
                        background: b.status === "paid" ? "rgba(22,163,74,0.12)" : b.status === "cancelled" ? "rgba(220,38,38,0.12)" : "rgba(234,179,8,0.12)",
                        color: b.status === "paid" ? "#16a34a" : b.status === "cancelled" ? "#dc2626" : "#ca8a04",
                      }}
                    >{b.status}</span>
                  </Td>
                  <Td>
                    <div className="flex flex-col gap-1">
                      <a href={`/ticket/${b.id}`} target="_blank" rel="noreferrer" className="bn-eyebrow underline" style={{ color: "var(--bn-accent)" }}>Ver</a>
                      {b.status !== "paid" && b.status !== "cancelled" && (
                        <button onClick={() => markPaid(b.id)} className="bn-eyebrow text-left underline" style={{ color: "#16a34a" }} data-testid={`mark-paid-${b.id}`}>
                          Marcar pagado
                        </button>
                      )}
                      {b.status === "paid" && (
                        <button onClick={() => resendTicket(b.id)} className="bn-eyebrow text-left underline" style={{ color: "var(--bn-text-muted)" }} data-testid={`resend-${b.id}`}>
                          Reenviar email
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 bn-eyebrow font-normal" style={{ color: "var(--bn-text-muted)" }}>{children}</th>;
}
function Td({ children }) {
  return <td className="px-4 py-4 align-top bn-body-font">{children}</td>;
}
function StatCard({ label, stats, icon: Icon }) {
  return (
    <div className="bn-card p-5">
      <div className="flex items-center justify-between">
        <div className="bn-eyebrow" style={{ color: "var(--bn-text-muted)" }}>{label}</div>
        <Icon size={16} style={{ color: "var(--bn-accent)" }} />
      </div>
      <div className="mt-4">
        <div className="bn-heading text-3xl">{stats.bookings}</div>
        <div className="text-xs mt-1" style={{ color: "var(--bn-text-muted)" }}>
          {stats.paid} pagadas · ${stats.revenue.toFixed(2)}
        </div>
      </div>
    </div>
  );
}
